import {
    type MutationFunctionContext,
    type UseMutationOptions,
    useMutation,
    useQueryClient,
} from "@tanstack/react-query";
import { api } from "api";
import type { PatientMergeRequest } from "api/requests";
import type { PatientMergeResult } from "api/responses";

type MergePatientOptions = Omit<UseMutationOptions<PatientMergeResult, Error, PatientMergeRequest>, "mutationFn">;

/**
 * Client hook for merging unlinked P/RP with PMS Patient.
 * @param options Options for the client call; provides callbacks for when the API call succeeds or fails.
 * @returns React Query mutation properties.
 */
export function useMergePatient(options?: MergePatientOptions) {
    const queryClient = useQueryClient();

    /** Provide built-in onSuccess behavior, then call provided callback if available. */
    const onSuccess = (
        data: PatientMergeResult,
        variables: PatientMergeRequest,
        onMutateResult: unknown,
        context: MutationFunctionContext,
    ) => {
        // Invalidate queries that are affected by linking a P/RP to a PMS patient
        void queryClient.invalidateQueries({ queryKey: ["patientActivity"] });
        void queryClient.invalidateQueries({ queryKey: ["searchPatientField"] });
        void queryClient.invalidateQueries({ queryKey: ["summary", variables.unlinkedPatientId] });
        void queryClient.invalidateQueries({ queryKey: ["pmsSummary", variables.pmsId] });

        // Use callback if provided
        return options?.onSuccess?.(data, variables, onMutateResult, context);
    };

    return useMutation({
        ...options,
        mutationFn: async (request: PatientMergeRequest) => await api.provider.mergePatient(request),
        onSuccess,
    });
}
